import React from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'react-qr-code';
import placeholder from '../assets/placeholder';
import { useLang } from '../LanguageContext';
import { LABEL_MODES, getLabelLines, getLabelText } from '../hooks/useLabelMode';
import { copyText } from '../utils/clipboard';

export default function CuttingCard({ cutting, labelMode = LABEL_MODES.NAME_AND_NUMBER }) {
  const { t } = useLang();
  const token = localStorage.getItem('token');

  const downloadPdf = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    try {
      const params = new URLSearchParams({ labelMode });
      const res = await fetch(`/api/qr/pdf/${cutting._id}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cutting_${cutting.code}_qr.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download QR PDF', err);
    }
  };

  const displayStrain = () => {
    const name = cutting.strainName || cutting.strain;
    const code = cutting.strainCode || cutting.strain;
    if (labelMode === LABEL_MODES.NAME_ONLY) return name;
    if (labelMode === LABEL_MODES.NUMBER_ONLY) return code;
    if (name && code && name !== code) return `${name} (${code})`;
    return name || code;
  };

  const qrBaseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
  const qrValue = `${qrBaseUrl}/${encodeURIComponent(cutting.code)}`;
  const labelLines = getLabelLines(
    labelMode,
    cutting.strainName || cutting.strain,
    cutting.code
  );

  const labelLineStyle = {
    wordBreak: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  };

  const displayValue = displayStrain();

  const handleCopy = async (event, value) => {
    event.preventDefault();
    event.stopPropagation();
    if (!value) return;
    await copyText(value);
  };

  return (
    <Link
      to={`/cutting/${cutting._id}`}
      className="card h-100 text-decoration-none text-dark"
    >
      {(() => {
        const main = Array.isArray(cutting.images)
          ? cutting.images.find((i) => i.isMain) || cutting.images[0]
          : null;
        const previewUrl =
          main?.type === 'video'
            ? main.thumbnailUrl || main.url
            : main?.url;
        if (main?.type === 'video') {
          return (
            <video
              src={main.url}
              poster={previewUrl || cutting.photo || placeholder}
              controls
              className="card-img-top object-fit-cover"
              style={{ aspectRatio: '1/1' }}
            />
          );
        }
        return (
          <img
            src={previewUrl || cutting.photo || placeholder}
            alt="preview"
            loading="lazy"
            className="card-img-top object-fit-cover"
            style={{ aspectRatio: '1/1' }}
          />
        );
      })()}
      <div className="card-body">
        <h5 className="card-title d-flex justify-content-between align-items-start">
          <span>{cutting.code}</span>
          <small className="text-muted">{cutting.shortId || cutting._id}</small>
        </h5>
        <p className="mb-2" style={labelLineStyle} title={displayValue}>{displayValue}</p>
        <div className="d-flex flex-column align-items-center mb-3">
          <QRCode value={qrValue} size={96} />
          <div className="small text-center mt-2" style={{ wordBreak: "break-word" }}>
            {labelLines.map((line, idx) => (
              <div key={idx} style={labelLineStyle} className="w-100" title={line}>
                {line}
              </div>
            ))}
          </div>
          <div className="d-flex flex-wrap gap-2 justify-content-center mt-2 w-100">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              aria-label={t('copyQrText')}
              onClick={(e) => handleCopy(e, qrValue)}
            >
              {t('copyQrText')}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              aria-label={t('copyLabelText')}
              onClick={(e) =>
                handleCopy(
                  e,
                  getLabelText(
                    labelMode,
                    cutting.strainName || cutting.strain,
                    cutting.code
                  )
                )
              }
            >
              {t('copyLabelText')}
            </button>
          </div>
        </div>
        <button
          onClick={downloadPdf}
          className="btn btn-sm btn-outline-secondary"
        >
          ?? {t('downloadQrPdf')}
        </button>
      </div>
    </Link>
  );
}
